import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { NativeSelect } from '../ui/NativeSelect';
import { Palette } from 'lucide-react';
import { TEMPLATE_CATEGORIES } from '@/types/lists';
import { useTemplatesStore } from '@/store/templatesStore';

// Simplified validation schema for templates only
const templateSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(500, 'Description must be less than 500 characters'),
  templateCategory: z.string().min(1, 'Template category is required'),
  color: z.string().optional(),
});

type TemplateFormInputs = z.infer<typeof templateSchema>;

interface TemplateFormProps {
  isOpen: boolean;
  onClose: () => void;
  templateId?: string;
  isEditing?: boolean;
}

// Predefined colors for visual organization
const PRESET_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#F7DC6F',
  '#BB8FCE', '#58D68D', '#F8C471', '#82E0AA'
];

// Color picker component
const ColorPicker = ({ value, onChange }: { value?: string; onChange: (color: string) => void }) => (
  <div className="flex gap-2 flex-wrap">
    {PRESET_COLORS.map(color => (
      <button
        key={color}
        type="button"
        className={`w-8 h-8 rounded-full border-2 hover:scale-110 transition-transform ${
          value === color ? 'border-gray-600' : 'border-gray-300'
        }`}
        style={{ backgroundColor: color }}
        onClick={() => onChange(color)}
        aria-label={`Select color ${color}`}
      />
    ))}
  </div>
);

export function TemplateForm({ isOpen, onClose, templateId, isEditing = false }: TemplateFormProps) {
  const { createTemplate, updateTemplate, templates } = useTemplatesStore();

  const template = templateId ? templates.find(t => t.id === templateId) : null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<TemplateFormInputs>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      title: '',
      description: '',
      templateCategory: '',
      color: '',
    }
  });

  // Populate form when editing
  useEffect(() => {
    if (isOpen && isEditing && template) {
      reset({
        title: template.title,
        description: template.description,
        templateCategory: template.category,
        color: template.color || '',
      });
    } else if (isOpen && !isEditing) {
      reset({
        title: '',
        description: '',
        templateCategory: '',
        color: '',
      });
    }
  }, [isOpen, isEditing, template, reset]);

  const handleFormSubmit = async (data: TemplateFormInputs) => {
    try {
      if (isEditing && templateId) {
        await updateTemplate(templateId, {
          title: data.title,
          description: data.description,
          category: data.templateCategory,
          color: data.color || undefined,
        });
      } else {
        await createTemplate({
          title: data.title,
          description: data.description,
          category: data.templateCategory,
          color: data.color || undefined,
        });
      }

      handleClose();
    } catch (error) {
      console.error(isEditing ? 'Error updating template:' : 'Error creating template:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleColorChange = (color: string) => {
    setValue('color', color);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title={isEditing ? 'Edit Template' : 'Create New Template'}
      description={isEditing
        ? 'Update the template details'
        : 'Create a reusable template that can be used to quickly generate new lists'
      }
      scrollBehavior="inside"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        {/* Title */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Title *</label>
          <Input
            {...register('title')}
            placeholder="e.g., Weekly Planning Template"
            error={errors.title?.message}
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Description *</label>
          <textarea
            {...register('description')}
            placeholder="Describe what this template is for and when to use it..."
            className="w-full min-h-[80px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
          />
          {errors.description && (
            <p className="text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        {/* Template Category */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Category *</label>
          <NativeSelect
            {...register('templateCategory')}
            error={errors.templateCategory?.message}
          >
            <option value="">Select category...</option>
            {TEMPLATE_CATEGORIES.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </NativeSelect>
          {errors.templateCategory && (
            <p className="text-sm text-red-600">{errors.templateCategory.message}</p>
          )}
        </div>

        {/* Color Theme */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <label className="text-sm font-medium">Color Theme (Optional)</label>
          </div>
          <ColorPicker
            value={watch('color')}
            onChange={handleColorChange}
          />
        </div>

        {/* Info text */}
        <p className="text-xs text-muted-foreground">
          {isEditing
            ? 'Note: Editing the template will not affect lists already created from it.'
            : 'After creating the template, you can add items to it from the template details page.'
          }
        </p>
      </form>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t mt-4 bg-background sticky bottom-0">
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(handleFormSubmit)}
        >
          {isSubmitting
            ? (isEditing ? 'Saving...' : 'Creating...')
            : (isEditing ? 'Save Changes' : 'Create Template')
          }
        </Button>
      </div>
    </Modal>
  );
}
